import React, { useEffect, useState } from "react";
import io from 'socket.io-client';
import { Container, Form, Button, Alert, Spinner, Col, Row, Modal, ListGroup } from "react-bootstrap";
import offerAPI from "../../services/offerapi";
import { Link } from "react-router-dom";
import downloadError from "./downloadError";

const DiscountCreate = () => {
    // console.log("DiscountCreate");
    const [file, setFile] = useState(null);
    const [fileUrl, setFileUrl] = useState(null); // Store the uploaded file URL
    const [uploadStatus, setUploadStatus] = useState("");
    const [isUploading, setIsUploading] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncResponse, setSyncResponse] = useState(null);
    const [showSyncModal, setShowSyncModal] = useState(false);
    const [rows, setRows] = useState([]); // Store processed rows here
    const [socket, setSocket] = useState(null);
    const [error, setError] = useState(null);
    const [syncStarted, setSyncStarted] = useState(false); // New state to track sync start

    useEffect(() => {
        let newSocket = null;

        // Start the socket connection only if syncStarted is true
        if (syncStarted) {
            // Create a new socket connection to the server
            // const socketURL = process.env.REACT_APP_BASE_URL_SOCKER_IO ?? 'http://localhost:3010';
            // newSocket = io(socketURL, {
            //     transports: ['websocket'], // Use WebSocket for better performance
            // });

            const socketURL = process.env.REACT_APP_BASE_URL_SOCKER_IO ?? 'http://localhost:3010';
            newSocket = io(socketURL, {
                transports: ['websocket'],
                withCredentials: true, // Important if using sessions
                path: '/socket.io',     // Default path — required if NGINX or reverse proxy is involved
            });

            // Set the socket to state
            setSocket(newSocket);

            // Listen for 'rowProcessed' event from the server
            newSocket.on('rowProcessed', (row) => {
                if (syncStarted) {
                    setRows((prevRows) => {
                        const updatedRows = [...prevRows, row];

                        // Sort the rows in descending order based on some property (e.g., timestamp or ID)
                        updatedRows.sort((a, b) => {
                            // First: sort errorType (non-zero comes before zero)
                            if (a.errorType !== b.errorType) {
                                return b.errorType - a.errorType;
                            }
                            // Then: sort by timestamp descending (most recent first)
                            return b.timestamp - a.timestamp;
                        });

                        return updatedRows;
                    });
                }
            });

            // Handle connection errors
            newSocket.on('connect_error', (err) => {
                console.error('Connection error:', err);
                setError('Connection failed. Please try again later.');
                newSocket.disconnect(); // Disconnect the socket on error
            });

            // Handle connection timeout
            newSocket.on('connect_timeout', () => {
                setError('Connection timed out. Please try again later.');
                newSocket.disconnect(); // Disconnect the socket if connection times out
            });

            // Handle socket disconnection
            newSocket.on('disconnect', (reason) => {
                if (reason === 'io server disconnect') {
                    setError('Server disconnected. Please try reconnecting.');
                }
            });

            // Cleanup the socket connection when the component unmounts or syncStarted changes
            return () => {
                if (newSocket) {
                    newSocket.close(); // Close the socket connection
                }
            };
        }

        // Return cleanup function if syncStarted is false
        return () => {
            if (newSocket) {
                newSocket.close(); // Close the socket if syncStarted becomes false
            }
        };

    }, [syncStarted]);  // We depend on syncStarted here to manage when rows are added

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile && selectedFile.type !== "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet") {
            setUploadStatus("Invalid file type. Please upload an .xlsx file.");
            setFile(null); // Reset file state if invalid
            setFileUrl(null); // Reset file URL
            return;
        }
        setFile(selectedFile);
        setFileUrl(null); // Reset file URL
        setUploadStatus(""); // Clear previous status
    };

    const handleUpload = async () => {
        if (!file) return;

        const formData = new FormData();
        formData.append("file", file); // Make sure the file is appended with the correct key.

        setIsUploading(true); // Set uploading state to true
        setUploadStatus("");  // Reset the previous upload status

        try {
            const resp = await offerAPI.uploadOffers(formData);
            setFileUrl(resp.data.fileUrl); // Store the file URL from the API response
            setUploadStatus(`${resp.message}`);
            setFile(null); // Reset file after successful upload
        } catch (error) {
            console.error(error);
            setUploadStatus(`Error uploading file: ${error.response?.data?.message || error.message}`);
            setFileUrl(null); // Reset file URL if upload fails
        } finally {
            setIsUploading(false); // Set uploading state to false when request finishes
        }
    };

    const handleSync = async () => {
        setShowSyncModal(false); // Close confirmation modal
        setIsSyncing(true);
        setSyncStarted(true); // Mark that syncing has started, which will allow row processing to be shown

        try {
            const resp = await offerAPI.syncOffers({ fileUrl }); // Send file URL with sync API
            setSyncResponse({ success: true, data: resp.data });
            setFileUrl(null);

        } catch (error) {
            const failedItems = error.response?.data?.failedItems || [];
            setSyncResponse({
                success: false,
                error: error.response?.data?.message || error.message,
                failedItems,
            });
        } finally {
            setIsSyncing(false);
        }
    };

    const uploadFiles = process.env.REACT_APP_IMAGE_URL + "/offerfiles/discountOffers.xlsx";

    return (
        <Container className="mt-5">
            <Row className="d-flex">
                <Col md={6}>
                    <h2 className="mb-4">Upload .xlsx File</h2>
                </Col>
                <Col md={6} className="text-end mt-2">
                    <Link className="btn btn-info text-white" to={uploadFiles} target="_blank">
                        Download Sample File
                    </Link>
                </Col>
            </Row>
            <Form>
                <Form.Group className="mb-3">
                    <Form.Label>Select .xlsx File</Form.Label>
                    <Form.Control
                        type="file"
                        accept=".xlsx"
                        onChange={handleFileChange}
                        aria-label="Upload an .xlsx file"
                    />
                </Form.Group>
                <Button
                    variant="primary"
                    onClick={handleUpload}
                    disabled={isUploading || !file}
                >
                    {isUploading ? <Spinner animation="border" size="sm" /> : "Upload"}
                </Button>
            </Form>
            {uploadStatus && (
                <Alert
                    className="mt-3"
                    variant={uploadStatus.startsWith("Error") ? "danger" : "success"}
                >
                    {uploadStatus}
                </Alert>
            )}
            <div className="mt-4">
                <Button
                    variant="secondary"
                    onClick={() => setShowSyncModal(true)}
                    disabled={!fileUrl || isUploading || isSyncing} // Enable only after successful upload
                >
                    {isSyncing ? <Spinner animation="border" size="sm" /> : "Sync Data"}
                </Button>
            </div>
            {syncResponse && (
                <Alert
                    className="mt-3"
                    variant={syncResponse.success ? "success" : "danger"}
                >
                    {syncResponse.success ? (
                        <p>Sync completed successfully.</p>
                    ) : (
                        <>
                            <p>Error during sync: {syncResponse.error}</p>
                            {syncResponse.failedItems.length > 0 && (
                                <ul>
                                    {syncResponse.failedItems.map((item, index) => (
                                        <li key={index}>{item}</li>
                                    ))}
                                </ul>
                            )}
                        </>
                    )}
                </Alert>
            )}
            {/* Sync Confirmation Modal */}
            <Modal show={showSyncModal} onHide={() => setShowSyncModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Sync</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to sync data? This action cannot be undone.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowSyncModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleSync}>
                        Confirm
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* List uploaded rows only after sync is started */}
            {syncStarted && rows.length > 0 && (
                <>
                    <Row className="align-items-center mt-4">
                        <Col>
                            <h3 className="mb-0">Processed Rows:</h3>
                        </Col>
                        <Col className="text-end">
                            {syncStarted && rows.some(row => row.errorType !== 0) && (
                                <Button
                                    variant="danger"
                                    onClick={() => downloadError(rows.filter(row => row.errorType !== 0))}
                                >
                                    Export Errors to CSV
                                </Button>
                            )}
                        </Col>
                    </Row>
                    <ListGroup>
                        {rows.map((row, index) => (
                            <ListGroup.Item key={index} className={row.errorType === 0 ? 'bg-success text-white' : 'bg-danger text-white'}>
                                {row.errorType === 2 && (<><h6>{row.rowMessage}</h6></>)}
                                {row.errorType === 1 && (<><h6>{row.rowMessage}</h6> {JSON.stringify(row.existingOfferMsg)}  <br /> </>)}
                                {JSON.stringify(row.data)}
                            </ListGroup.Item>
                        ))}
                    </ListGroup>
                </>
            )}


        </Container>
    );
};

export default DiscountCreate;
